import * as AgenticV2API from "@/services/api/agentic-v2";
import type { Artifact } from "@/services/api/agentic-v2";
import type { OpenWindowConfig } from "@/components/ui/os-layout/useWindow";
import { isReactFormLoaArtifact } from "@/components/clients/clientDisplay";
import { preloadOsApp } from "@/components/ui/os-layout/osAppPrefetch";
import { artifactReportViewerUrl } from "./artifactReportViewer";

function warmArtifactWindowChunk(): void {
  void preloadOsApp("artifact");
}

export type OpenWindowFn = (config: OpenWindowConfig) => void;

export type OpenArtifactMode = "view" | "edit" | "review";

/** How the artefact surface was opened (OS window vs external browser tab). */
export type OpenArtifactSurface = "os_window" | "external_tab";

export interface OpenArtifactDestinationOptions {
  mode?: OpenArtifactMode;
  title?: string;
  taskId?: string;
}

/** True when the artefact's primary surface is report-builder (not a TipTap/PDF doc). */
export function isReportBuilderArtifact(artifact: Artifact): boolean {
  return Boolean(
    artifact.storage?.report_builder?.report_id ?? artifactReportViewerUrl(artifact),
  );
}

/** Open the correct surface for an artefact (viewer tab, report builder, or document editor). */
export function openArtifactDestination(
  artifact: Artifact,
  openWindow: OpenWindowFn,
  options: OpenArtifactDestinationOptions = {},
): OpenArtifactSurface {
  const mode = options.mode ?? "view";
  const title = (options.title ?? artifact.filename) || "Artefact";

  if (mode === "view") {
    // Editable LOAs: open the artefact app (form + preview tabs), not the
    // stored PDF blob — advisers edit fields and re-render from there.
    if (isReactFormLoaArtifact(artifact)) {
      warmArtifactWindowChunk();
      openWindow({
        appId: "artifact",
        title,
        params: { id: artifact._id },
      });
      return "os_window";
    }

    const viewerUrl = artifactReportViewerUrl(artifact);
    if (viewerUrl) {
      window.open(viewerUrl, "_blank", "noopener,noreferrer");
      return "external_tab";
    }
  }

  if (mode === "edit") {
    const reportId = artifact.storage?.report_builder?.report_id;
    if (reportId) {
      openWindow({
        appId: "report-builder",
        title,
        params: { report_id: reportId },
      });
      return "os_window";
    }
  }

  const taskId = options.taskId ?? artifact.generated_by?.task_id;
  warmArtifactWindowChunk();
  openWindow({
    appId: "artifact",
    title,
    params: {
      id: artifact._id,
      ...(mode === "view" ? { mode: "read" } : { mode }),
      ...(taskId ? { taskId } : {}),
    },
  });
  return "os_window";
}

/** Resolve artefact metadata then open the correct surface. */
export async function openArtifactById(
  artifactId: string,
  openWindow: OpenWindowFn,
  options: OpenArtifactDestinationOptions = {},
): Promise<OpenArtifactSurface> {
  const [artifact] = await Promise.all([
    AgenticV2API.getArtifact(artifactId),
    preloadOsApp("artifact"),
  ]);
  return openArtifactDestination(artifact, openWindow, options);
}
